// Intake forms and online-visit questionnaires are shown in the same patient-chart section.
// They come from two endpoints with different shapes:
//   - intake forms:    { IntakeFormId, Name, CompleteDateTime, VisitTypeName }
//   - questionnaires:  { QuestionnaireSessionId, QuestionnaireKey, CompleteDateTime, VisitTypeName }
// Questionnaire (online-visit) items have no Name, so we synthesize one with an "(Online)" suffix.
// The `isQuestionnaire` flag drives the link target when an item is clicked.

export const ONLINE_VISIT_LABEL_SUFFIX = '(Online)'
export const ONLINE_VISIT_FALLBACK_LABEL = 'Online visit'

type IntakeFormListItem = {
  IntakeFormId?: string | null
  Name?: string | null
  CompleteDateTime?: string | null
  VisitTypeName?: string | null
}

type QuestionnaireListItem = {
  QuestionnaireSessionId?: string | null
  QuestionnaireKey?: string | null
  CompleteDateTime?: string | null
  VisitTypeName?: string | null
}

export type IntakeFormsAndQuestionnairesItem =
  | (IntakeFormListItem & { isQuestionnaire: false })
  | (QuestionnaireListItem & { isQuestionnaire: true; Name: string })

const toTime = (value?: string | null): number => (value ? new Date(value).getTime() : 0)

export const getOnlineVisitLabel = (visitTypeName?: string | null): string =>
  visitTypeName ? `${visitTypeName} ${ONLINE_VISIT_LABEL_SUFFIX}` : ONLINE_VISIT_FALLBACK_LABEL

/**
 * Merges the intake-forms and questionnaire (online-visit) lists into one list for the
 * patient-chart intake section, tagging each item by type and sorting newest-completed first.
 */
export const mergeIntakeFormsAndQuestionnaires = (
  intakeForms: IntakeFormListItem[] = [],
  questionnaires: QuestionnaireListItem[] = [],
): IntakeFormsAndQuestionnairesItem[] => {
  const intakeItems: IntakeFormsAndQuestionnairesItem[] = (intakeForms || []).map((item) => ({
    ...item,
    isQuestionnaire: false,
  }))

  const questionnaireItems: IntakeFormsAndQuestionnairesItem[] = (questionnaires || []).map((item) => ({
    ...item,
    isQuestionnaire: true,
    Name: getOnlineVisitLabel(item.VisitTypeName),
  }))

  return [...intakeItems, ...questionnaireItems].sort((a, b) => toTime(b.CompleteDateTime) - toTime(a.CompleteDateTime))
}
